import React, { useState, useEffect } from 'react';
import { useKpros } from '../../../context/KprosContext';
import { useTheme } from '../../../context/ThemeContext';
import { Search, Plus, Edit2, Trash2, X, Loader2, BookmarkCheck } from 'lucide-react';
import toast from 'react-hot-toast';

const FEATURE_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'chat', label: 'Chat' },
  { value: 'studio', label: 'Studio' },
  { value: 'ait', label: 'AIT' },
  { value: 'data_extraction', label: 'Data Extraction' },
  { value: 'profitability', label: 'Analyzer' },
  { value: 'team_chat', label: 'Team Chat' },
  { value: 'email', label: 'Email' },
];

const CATEGORY_OPTIONS = FEATURE_OPTIONS.filter(f => f.value !== '');

const FEATURE_LABELS = Object.fromEntries(FEATURE_OPTIONS.map(f => [f.value, f.label]));

function PromptForm({ prompt, onSave, onCancel }) {
  const { theme } = useTheme();
  const [title, setTitle] = useState(prompt?.title || '');
  const [content, setContent] = useState(prompt?.content || '');
  const [featureTag, setFeatureTag] = useState(prompt?.feature_tag || 'chat');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    setSaving(true);
    const success = await onSave({ title: title.trim(), content: content.trim(), feature_tag: featureTag });
    setSaving(false);
    if (success) onCancel();
  };

  return (
    <form onSubmit={handleSubmit} className={`rounded-lg border p-4 space-y-3 ${
      theme === 'dark' ? 'bg-gray-750 border-gray-600' : 'bg-gray-50 border-gray-200'
    }`}>
      <div>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 font-rasa">Title</label>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          className={`w-full text-sm px-3 py-2 rounded-lg border font-rasa ${
            theme === 'dark' ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200 bg-white text-gray-800'
          } focus:outline-none focus:ring-1 focus:ring-blue-500`}
          placeholder="Prompt title..."
          maxLength={200}
          required
          autoFocus
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 font-rasa">Content</label>
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          rows={4}
          className={`w-full text-sm px-3 py-2 rounded-lg border font-rasa resize-y ${
            theme === 'dark' ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200 bg-white text-gray-800'
          } focus:outline-none focus:ring-1 focus:ring-blue-500`}
          placeholder="Write your prompt..."
          required
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 font-rasa">Category</label>
        <select
          value={featureTag}
          onChange={e => setFeatureTag(e.target.value)}
          className={`w-full text-sm px-3 py-2 rounded-lg border font-rasa ${
            theme === 'dark' ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200 bg-white text-gray-800'
          } focus:outline-none focus:ring-1 focus:ring-blue-500`}
        >
          {CATEGORY_OPTIONS.map(f => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-rasa"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !title.trim() || !content.trim()}
          className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed font-rasa"
        >
          {saving && <Loader2 size={14} className="animate-spin" />}
          {prompt ? 'Update' : 'Save'}
        </button>
      </div>
    </form>
  );
}

function PromptsSettings() {
  const { theme } = useTheme();
  const {
    savedPrompts, savedPromptsLoading, fetchSavedPrompts,
    savePrompt, updateSavedPrompt, deleteSavedPrompt,
  } = useKpros();

  const [search, setSearch] = useState('');
  const [featureFilter, setFeatureFilter] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);

  useEffect(() => {
    fetchSavedPrompts();
  }, [fetchSavedPrompts]);

  const filtered = savedPrompts.filter(p => {
    if (featureFilter && p.feature_tag !== featureFilter) return false;
    if (search) {
      const term = search.toLowerCase();
      return p.title.toLowerCase().includes(term) || p.content.toLowerCase().includes(term);
    }
    return true;
  });

  const handleCreate = async (data) => {
    const success = await savePrompt(data);
    if (success) {
      toast.success('Prompt created');
      setShowNewForm(false);
    } else {
      toast.error('Failed to create prompt');
    }
    return success;
  };

  const handleUpdate = async (data) => {
    const success = await updateSavedPrompt(editingPrompt.id, data);
    if (success) {
      toast.success('Prompt updated');
      setEditingPrompt(null);
    } else {
      toast.error('Failed to update prompt');
    }
    return success;
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this prompt?')) return;
    const success = await deleteSavedPrompt(id);
    if (success) toast.success('Prompt deleted');
    else toast.error('Failed to delete prompt');
  };

  return (
    <div className="space-y-6">
      <div className={`rounded-lg p-6 shadow-sm border ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-dm-serif font-semibold">Saved Prompts</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-rasa mt-1">
              Create and manage reusable prompts for your chats
            </p>
          </div>
          {!showNewForm && (
            <button
              onClick={() => { setShowNewForm(true); setEditingPrompt(null); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 font-rasa"
            >
              <Plus size={14} />
              New Prompt
            </button>
          )}
        </div>

        {/* New prompt form */}
        {showNewForm && (
          <div className="mb-4">
            <PromptForm onSave={handleCreate} onCancel={() => setShowNewForm(false)} />
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search prompts..."
              className={`w-full pl-8 pr-3 py-2 text-sm rounded-lg border font-rasa ${
                theme === 'dark' ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200 bg-gray-50 text-gray-800'
              } focus:outline-none focus:ring-1 focus:ring-blue-500`}
            />
          </div>
          <select
            value={featureFilter}
            onChange={e => setFeatureFilter(e.target.value)}
            className={`text-sm px-3 py-2 rounded-lg border font-rasa ${
              theme === 'dark' ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200 bg-gray-50 text-gray-800'
            } focus:outline-none focus:ring-1 focus:ring-blue-500`}
          >
            {FEATURE_OPTIONS.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>

        {/* Prompts list */}
        {savedPromptsLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-400 dark:text-gray-500 font-rasa">
            {savedPrompts.length === 0
              ? 'No saved prompts yet. Click "New Prompt" to create one.'
              : 'No prompts match your filter.'}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(prompt => (
              <div key={prompt.id}>
                {editingPrompt?.id === prompt.id ? (
                  <PromptForm
                    prompt={prompt}
                    onSave={handleUpdate}
                    onCancel={() => setEditingPrompt(null)}
                  />
                ) : (
                  <div className={`group p-4 rounded-lg border transition-colors ${
                    theme === 'dark'
                      ? 'border-gray-700 hover:border-gray-600'
                      : 'border-gray-100 hover:border-gray-300'
                  }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 font-rasa truncate">
                            {prompt.title}
                          </h4>
                          {prompt.feature_tag && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-rasa ${
                              theme === 'dark' ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {FEATURE_LABELS[prompt.feature_tag] || prompt.feature_tag}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-rasa line-clamp-2 whitespace-pre-wrap">
                          {prompt.content}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => { setEditingPrompt(prompt); setShowNewForm(false); }}
                          className="p-1.5 text-gray-400 hover:text-blue-500 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(prompt.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PromptsSettings;
